// src/components/admin/CursosAdmin.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
    Box, Paper, Typography, TextField, Button, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, Grid, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TablePagination,
    Tooltip, Chip, Stack, MenuItem, Select, InputLabel, FormControl,
    Tabs, Tab
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import GetAppIcon from "@mui/icons-material/GetApp";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InscripcionesAdmin from "./InscripcionesAdmin";
import MaestrosAdmin from "./MaestrosAdmin";

const API = process.env.REACT_APP_API_URL;

const headersAuth = () => {
    const token = localStorage.getItem("token");
    return {
        Authorization: token ? `Bearer ${token}` : undefined,
        "Content-Type": "application/json",
    };
};

// 🔸 Cambia esto si tu ruta es distinta
const buildInscripcionesUrl = (cursoId) => `${API}/api/inscripciones/curso/${cursoId}`;

// ========================= Yup / DTOs =========================
const CursoSchema = Yup.object().shape({
    codigo: Yup.number().integer().min(0).nullable(),
    nombre: Yup.string().required("Requerido"),
    fechaDesde: Yup.string().nullable(),
    fechaHasta: Yup.string().nullable(),
    lugar: Yup.string().nullable(),
    importe: Yup.number().min(0).nullable(),
    numeroAlumnos: Yup.number().integer().min(0).nullable(),
    topeAlumnos: Yup.number().integer().min(0).nullable(),
    horas: Yup.number().integer().min(0).nullable(),
    observaciones: Yup.string().nullable(),
    maestro: Yup.number().integer().nullable(),
    disponible: Yup.number().oneOf([0, 1]).required("Requerido"),
});

const defaultValues = {
    id: null,
    codigo: null,
    nombre: "",
    fechaDesde: "",
    fechaHasta: "",
    lugar: "",
    importe: null,
    numeroAlumnos: null,
    topeAlumnos: null,
    horas: null,
    observaciones: "",
    maestro: null,
    disponible: 1,
};

// Back -> Front (snake_case → camelCase amigable)
const normCurso = (c) => ({
    id: c.id,
    codigo: c.codigo ?? null,
    nombre: c.nombre ?? "",
    fechaDesde: c.fecha_desde ?? c.fechaDesde ?? "",
    fechaHasta: c.fecha_hasta ?? c.fechaHasta ?? "",
    lugar: c.lugar ?? "",
    importe: c.importe ?? null,
    numeroAlumnos: c.numero_alumnos ?? c.numeroAlumnos ?? null,
    topeAlumnos: c.tope_alumnos ?? c.topeAlumnos ?? null,
    horas: c.horas ?? null,
    observaciones: c.observaciones ?? "",
    maestro: c.maestro_id ?? c.maestro ?? c?.maestro?.id ?? null,
    disponible: c.disponible ?? 1,
});

// Front -> Back (camelCase → snake_case)
const denormCurso = (v) => ({
    codigo: v.codigo ?? null,
    nombre: v.nombre,
    fecha_desde: v.fechaDesde || null,
    fecha_hasta: v.fechaHasta || null,
    lugar: v.lugar || null,
    importe: v.importe ?? null,
    numero_alumnos: v.numeroAlumnos ?? null,
    tope_alumnos: v.topeAlumnos ?? null,
    horas: v.horas ?? null,
    observaciones: v.observaciones || null,
    maestro: v.maestro ?? null,
    disponible: v.disponible ?? 1,
});

const maestroNombre = (maestros, id) => {
    if (id == null) return "-";
    const m = maestros.find((x) => String(x.id) === String(id));
    if (!m) return id;
    const ap1 = m.apellido_1 || m.apellido1 || "";
    const ap2 = m.apellido_2 || m.apellido2 || "";
    return [m.nombre, ap1, ap2].filter(Boolean).join(" ");
};

const normMaestro = (m) => ({
    id: m.id,
    nombre: m.nombre ?? "",
    apellido_1: m.apellido_1 ?? m.apellido1 ?? "",
    apellido_2: m.apellido_2 ?? m.apellido2 ?? "",
});

const fetchMaestros = async () => {
    try {
        const { data } = await axios.get(`${API}/api/maestros`, { headers: headersAuth() });
        const arr = Array.isArray(data)
            ? data
            : Array.isArray(data?.content)
                ? data.content
                : Array.isArray(data?.data)
                    ? data.data
                    : [];

        setMaestros(arr.map(normMaestro));
    } catch (e) {
        console.error("Error cargando maestros:", e);
        setMaestros([]);
    }
};

// ---------- Normalizador de inscritos (colegiados) para export ----------
const normInscrito = (ins) => {
    const c = ins.colegiado || ins.colegido || {}; // compatibilidad
    const cod = c.codigo ?? c.id ?? ins.colegiado_id ?? "";
    const nombre = c.nombre ?? ins.nombre ?? "";
    const ap1 = c.apellido_1 ?? c.apellido1 ?? ins.apellido_1 ?? ins.apellido1 ?? "";
    const ap2 = c.apellido_2 ?? c.apellido2 ?? ins.apellido_2 ?? ins.apellido2 ?? "";
    const email = c.email ?? ins.email ?? "";
    const tel = c.telefono_1 ?? c.telefono1 ?? ins.telefono_1 ?? ins.telefono1 ?? "";
    return { codigo: cod, nombre, apellido_1: ap1, apellido_2: ap2, email, telefono: tel };
};

// =============== Helpers: ordenar y formatear ===============
/**
 * Ordena cursos poniendo SIEMPRE primero los disponibles (1),
 * después los no disponibles (0). Como segundo criterio,
 * ordena por fechaDesde ascendente (vacíos al final).
 */
const sortCursos = (arr) => {
    const valDisp = (d) => (d == null ? 0 : Number(d));
    const valFecha = (f) => (f ? f : "9999-12-31"); // vacíos al final
    return [...arr].sort((a, b) => {
        const dB = valDisp(b.disponible);
        const dA = valDisp(a.disponible);
        if (dB !== dA) return dB - dA; // 1 antes que 0
        const fa = valFecha(a.fechaDesde);
        const fb = valFecha(b.fechaDesde);
        return fa.localeCompare(fb); // asc
    });
};

function AlumnosCount({ cursoId }) {
    const [count, setCount] = useState(null);
    const cacheRef = useRef({});

    useEffect(() => {
        let alive = true;
        const run = async () => {
            if (cacheRef.current[cursoId] != null) {
                setCount(cacheRef.current[cursoId]);
                return;
            }
            try {
                const { data } = await axios.get(buildInscripcionesUrl(cursoId), { headers: headersAuth() });
                const n = Array.isArray(data) ? data.length : (data?.content?.length ?? data?.totalElements ?? 0);
                cacheRef.current[cursoId] = n;
                if (alive) setCount(n);
            } catch {
                if (alive) setCount(0);
            }
        };
        run();
        return () => { alive = false; };
    }, [cursoId]);

    return <span>{count == null ? "…" : count}</span>;
}

// =================== Filtros con “Disponible” ===================
function Filtros({ onBuscar, loading }) {
    const [nombre, setNombre] = useState("");
    const [desde, setDesde] = useState("");
    const [hasta, setHasta] = useState("");
    const [disp, setDisp] = useState(""); // "", "1", "0"

    const handleBuscar = () =>
        onBuscar({
            nombre: nombre || null,
            desde: desde || null,
            hasta: hasta || null,
            disponible: disp === "" ? null : Number(disp),
        });

    const handleLimpiar = () => {
        setNombre("");
        setDesde("");
        setHasta("");
        setDisp("");
        onBuscar({ nombre: null, desde: null, hasta: null, disponible: null, limpiar: true });
    };

    return (
        <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={4}>
                <TextField
                    label="Nombre contiene..."
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    fullWidth
                    size="small"
                />
            </Grid>

            <Grid item xs={12} sm={2}>
                <TextField
                    label="Desde"
                    type="date"
                    value={desde}
                    onChange={(e) => setDesde(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>

            <Grid item xs={12} sm={2}>
                <TextField
                    label="Hasta"
                    type="date"
                    value={hasta}
                    onChange={(e) => setHasta(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>

            <Grid item xs={12} sm={2}>
                <FormControl fullWidth size="small">
                    <InputLabel id="disp-filter-label">Disponibles</InputLabel>
                    <Select
                        labelId="disp-filter-label"
                        label="Disponibles"
                        value={disp}
                        onChange={(e) => setDisp(e.target.value)}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="1">Disponibles</MenuItem>
                        <MenuItem value="0">No disponibles</MenuItem>
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12} sm={2} display="flex" gap={1}>
                <Button onClick={handleBuscar} variant="contained" disabled={loading} fullWidth>Buscar</Button>
                <Button onClick={handleLimpiar} variant="outlined" disabled={loading} fullWidth>Limpiar</Button>
            </Grid>
        </Grid>
    );
}

// =================== Diálogo Crear/Editar ===================
function DialogCurso({ open, onClose, initialValues, onSubmit, maestros }) {
    const isEdit = Boolean(initialValues?.id);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{isEdit ? "Editar curso" : "Nuevo curso"}</DialogTitle>

            <Formik
                initialValues={initialValues || defaultValues}
                enableReinitialize
                validationSchema={CursoSchema}
                onSubmit={async (values, { setSubmitting }) => {
                    try {
                        const payload = { ...values };

                        ["fechaDesde", "fechaHasta", "lugar", "observaciones"].forEach(k => {
                            if (payload[k] === "") payload[k] = null;
                        });
                        ["codigo", "importe", "numeroAlumnos", "topeAlumnos", "horas", "maestro"].forEach(k => {
                            if (payload[k] === "" || payload[k] === undefined) payload[k] = null;
                            if (payload[k] !== null) payload[k] = Number(payload[k]);
                        });

                        await onSubmit(payload);
                        onClose();
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ errors, touched, isSubmitting, values, handleChange, setFieldValue }) => (
                    <Form>
                        <DialogContent dividers>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        name="codigo"
                                        label="Código"
                                        type="number"
                                        value={values.codigo ?? ""}
                                        onChange={handleChange}
                                        fullWidth
                                        error={touched.codigo && Boolean(errors.codigo)}
                                        helperText={touched.codigo && errors.codigo}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={9}>
                                    <TextField
                                        name="nombre"
                                        label="Nombre"
                                        value={values.nombre}
                                        onChange={handleChange}
                                        fullWidth
                                        error={touched.nombre && Boolean(errors.nombre)}
                                        helperText={touched.nombre && errors.nombre}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="fechaDesde"
                                        label="Fecha desde"
                                        type="date"
                                        value={values.fechaDesde || ""}
                                        onChange={handleChange}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="fechaHasta"
                                        label="Fecha hasta"
                                        type="date"
                                        value={values.fechaHasta || ""}
                                        onChange={handleChange}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        name="lugar"
                                        label="Lugar"
                                        value={values.lugar || ""}
                                        onChange={handleChange}
                                        fullWidth
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth>
                                        <InputLabel id="disp-label">Disponible</InputLabel>
                                        <Select
                                            labelId="disp-label"
                                            label="Disponible"
                                            value={values.disponible}
                                            onChange={(e) => setFieldValue("disponible", Number(e.target.value))}
                                        >
                                            <MenuItem value={0}>NO</MenuItem>
                                            <MenuItem value={1}>SÍ</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        name="importe"
                                        label="Importe (€)"
                                        type="number"
                                        inputProps={{ step: "0.01" }}
                                        value={values.importe ?? ""}
                                        onChange={handleChange}
                                        fullWidth
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        name="numeroAlumnos"
                                        label="Nº alumnos (informativo)"
                                        type="number"
                                        value={values.numeroAlumnos ?? ""}
                                        onChange={handleChange}
                                        fullWidth
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        name="topeAlumnos"
                                        label="Tope alumnos"
                                        type="number"
                                        value={values.topeAlumnos ?? ""}
                                        onChange={handleChange}
                                        fullWidth
                                    />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        name="horas"
                                        label="Horas"
                                        type="number"
                                        value={values.horas ?? ""}
                                        onChange={handleChange}
                                        fullWidth
                                    />
                                </Grid>

                                <Grid item xs={12} sm={8}>
                                    <FormControl
                                        fullWidth
                                        error={touched.maestro && Boolean(errors.maestro)}
                                    >
                                        <InputLabel id="maestro-label">Profesor</InputLabel>
                                        <Select
                                            labelId="maestro-label"
                                            label="Profesor"
                                            value={values.maestro ?? ""}
                                            onChange={(e) =>
                                                setFieldValue("maestro", e.target.value === "" ? null : Number(e.target.value))
                                            }
                                        >
                                            <MenuItem value="">— Selecciona profesor —</MenuItem>
                                            {maestros.map((m) => (
                                                <MenuItem key={m.id} value={m.id}>
                                                    {[m.nombre, m.apellido_1, m.apellido_2].filter(Boolean).join(" ")}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {touched.maestro && errors.maestro && (
                                            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                                                {errors.maestro}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        name="observaciones"
                                        label="Observaciones"
                                        value={values.observaciones || ""}
                                        onChange={handleChange}
                                        fullWidth
                                        multiline
                                        minRows={3}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={onClose} disabled={isSubmitting}>Cancelar</Button>
                            <Button type="submit" variant="contained" disabled={isSubmitting}>
                                {isEdit ? "Guardar cambios" : "Crear curso"}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
}

// =================== Componente principal ===================
export default function CursosAdmin() {
    const [tab, setTab] = useState(0);
    const [rows, setRows] = useState([]);
    const [maestros, setMaestros] = useState([]);
    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [openDialog, setOpenDialog] = useState(false);
    const [editing, setEditing] = useState(null);

    const [openIns, setOpenIns] = useState(false);
    const [cursoSel, setCursoSel] = useState(null);

    // ------- export general cursos -------
    const toCsvCursos = (rowsData) => {
        const header = [
            "ID", "Código", "Nombre", "Fecha desde", "Fecha hasta", "Lugar",
            "Importe", "Nº alumnos (dinámico)", "Tope", "Horas", "Maestro",
            "Disponible", "Observaciones"
        ];

        const lines = rowsData.map(r => {
            const maestro = maestroNombre(maestros, r.maestro) || "";
            const disponible = r.disponible === 1 ? "SÍ" : "NO";
            const vals = [
                r.id ?? "", r.codigo ?? "", r.nombre ?? "", r.fechaDesde || "",
                r.fechaHasta || "", r.lugar || "", r.importe ?? "", "",
                r.topeAlumnos ?? "", r.horas ?? "", maestro, disponible, r.observaciones || ""
            ];
            return vals.map(v => `"${String(v).replace(/"/g, '""')}"`).join(";");
        });

        return "\uFEFF" + [header.join(";"), ...lines].join("\n");
    };

    const handleExportCursos = () => {
        if (!rows.length) {
            alert("No hay cursos para exportar.");
            return;
        }
        const csv = toCsvCursos(rows);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "cursos.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };

    // (Mantenemos helpers de inscritos por si los vuelves a usar en el futuro)
    const toCsvInscritos = (inscritos) => {
        const header = ["Código", "Nombre", "Apellido 1", "Apellido 2", "Email", "Teléfono"];
        const lines = inscritos
            .map(normInscrito)
            .map(i =>
                [i.codigo ?? "", i.nombre ?? "", i.apellido_1 ?? "", i.apellido_2 ?? "", i.email ?? "", i.telefono ?? ""]
                    .map(v => `"${String(v).replace(/"/g, '""')}"`)
                    .join(";")
            );
        return "\uFEFF" + [header.join(";"), ...lines].join("\n");
    };

    const fetchMaestros = async () => {
        try {
            const { data } = await axios.get(`${API}/api/maestros`, { headers: headersAuth() });
            console.log("Respuesta /api/maestros:", data);

            const arr = Array.isArray(data)
                ? data
                : Array.isArray(data?.content)
                    ? data.content
                    : Array.isArray(data?.data)
                        ? data.data
                        : [];

            setMaestros(arr.map(normMaestro));
        } catch (e) {
            console.error("Error cargando maestros:", e);
            setMaestros([]);
        }
    };

    const fetchCursos = async () => {
        const { data } = await axios.get(`${API}/api/cursos`, { headers: headersAuth() });
        const arr = (data || []).map(normCurso);
        setRows(sortCursos(arr)); // ⬅️ ordenar: disponibles primero
    };

    const fetchAll = async () => {
        setLoading(true);
        try {
            await Promise.all([fetchCursos(), fetchMaestros()]);
        } catch (e) {
            console.error(e);
            alert("No se pudieron cargar cursos/maestros.");
        } finally {
            setLoading(false);
        }
    };

    // Buscador (incluye disponible)
    const buscar = async ({ nombre, desde, hasta, disponible, limpiar = false }) => {
        setLoading(true);
        try {
            if (limpiar) {
                await fetchAll();
                setPage(0);
                return;
            }
            const params = new URLSearchParams();
            if (nombre) params.append("nombre", nombre);
            if (desde) params.append("desde", desde);
            if (hasta) params.append("hasta", hasta);
            if (typeof disponible === "number") params.append("disponible", String(disponible)); // si tu backend lo soporta

            const url = `${API}/api/cursos/buscar${params.toString() ? "?" + params.toString() : ""}`;
            const { data } = await axios.get(url, { headers: headersAuth() });
            let arr = (data || []).map(normCurso);

            // Fallback por si el backend aún no filtra por "disponible"
            if (typeof disponible === "number") {
                arr = arr.filter((r) => Number(r.disponible ?? 0) === disponible);
            }

            setRows(sortCursos(arr)); // ⬅️ ordenar: disponibles primero
            setPage(0);
        } catch (e) {
            console.error(e);
            alert("Error buscando cursos.");
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditing(null);
        setOpenDialog(true);
    };

    const handleEdit = (curso) => {
        const c = normCurso(curso);
        setEditing({ ...defaultValues, ...c });
        setOpenDialog(true);
    };

    const handleDelete = async (curso) => {
        if (!window.confirm(`¿Eliminar el curso "${curso.nombre}"?`)) return;
        try {
            await axios.delete(`${API}/api/cursos/${curso.id}`, { headers: headersAuth() });
            setRows((prev) => sortCursos(prev.filter((r) => r.id !== curso.id)));
        } catch (e) {
            console.error(e);
            alert("No se pudo eliminar el curso.");
        }
    };

    const submitCurso = async (payload) => {
        if (!payload.codigo) {
            const maxCodigo = rows.length > 0 ? Math.max(...rows.map((r) => r.codigo || 0)) : 0;
            payload.codigo = maxCodigo + 1;
        }

        const body = denormCurso(payload);
        console.log("Payload curso a enviar:", body);

        try {
            if (payload.id) {
                const { data } = await axios.put(`${API}/api/cursos/${payload.id}`, body, { headers: headersAuth() });
                setRows((prev) => sortCursos(prev.map((r) => (r.id === payload.id ? normCurso(data) : r))));
            } else {
                const { data } = await axios.post(`${API}/api/cursos`, body, { headers: headersAuth() });
                setRows((prev) => sortCursos([normCurso(data), ...prev]));
            }
        } catch (err) {
            console.error("Error guardando curso:", err);
            console.error("Respuesta backend:", err?.response?.data);
            alert(
                typeof err?.response?.data === "string"
                    ? err.response.data
                    : err?.response?.data?.message || "No se pudo guardar el curso"
            );
            throw err;
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const paginated = useMemo(() => {
        const start = page * rowsPerPage;
        return rows.slice(start, start + rowsPerPage);
    }, [rows, page, rowsPerPage]);

    const fmt = (d) => (d ? d : "-");

    return (
        <Box>
            {/* ------------ TABS ------------ */}
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Cursos" />
                <Tab label="Profesores" />
            </Tabs>

            {/* ------------ TAB: CURSOS ------------ */}
            {tab === 0 && (
                <>
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <Typography variant="h5" fontWeight={700}>Cursos</Typography>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Button variant="outlined" startIcon={<GetAppIcon />} onClick={handleExportCursos}>
                                    Exportar
                                </Button>
                                <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
                                    Nuevo curso
                                </Button>
                            </Box>
                        </Box>

                        {/* Filtros: incluye filtro por Disponible */}
                        <Filtros onBuscar={buscar} loading={loading} />
                    </Paper>

                    <Paper sx={{ overflow: "hidden" }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {/* ⬇️ Columnas */}
                                        <TableCell>Código</TableCell>
                                        <TableCell>Nombre</TableCell>
                                        <TableCell>Fechas</TableCell>
                                        <TableCell>Profesor</TableCell>
                                        <TableCell>Disponible</TableCell>
                                        <TableCell align="right">Acciones</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {paginated.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{row.codigo ?? "-"}</TableCell>

                                            <TableCell>
                                                <Stack spacing={0.5}>
                                                    <Typography variant="body2" fontWeight={600}>{row.nombre}</Typography>
                                                    {row.observaciones ? (
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: "vertical",
                                                                overflow: "hidden",
                                                            }}
                                                        >
                                                            {row.observaciones}
                                                        </Typography>
                                                    ) : null}
                                                </Stack>
                                            </TableCell>

                                            <TableCell>
                                                <Stack spacing={0.5}>
                                                    <Typography variant="body2">{fmt(row.fechaDesde)}</Typography>
                                                    <Typography variant="body2">→ {fmt(row.fechaHasta)}</Typography>
                                                </Stack>
                                            </TableCell>

                                            <TableCell>{maestroNombre(maestros, row.maestro)}</TableCell>

                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={row.disponible === 1 ? "SÍ" : "NO"}
                                                    color={row.disponible === 1 ? "success" : "default"}
                                                    variant="outlined"
                                                />
                                            </TableCell>

                                            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        setCursoSel(row.id);
                                                        setOpenIns(true);
                                                    }}
                                                >
                                                    Ver inscripciones
                                                </Button>

                                                <Tooltip title="Editar">
                                                    <IconButton onClick={() => handleEdit(row)} size="small">
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Eliminar">
                                                    <IconButton onClick={() => handleDelete(row)} size="small" color="error">
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {paginated.length === 0 && !loading && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                                    No hay cursos que mostrar.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={rows.length}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                        />
                    </Paper>

                    <DialogCurso
                        open={openDialog}
                        onClose={() => setOpenDialog(false)}
                        initialValues={editing || defaultValues}
                        onSubmit={submitCurso}
                        maestros={maestros}
                    />

                    <Dialog open={openIns} onClose={() => setOpenIns(false)} fullWidth maxWidth="lg">
                        <DialogTitle>
                            Inscripciones — {rows.find((r) => r.id === cursoSel)?.nombre || cursoSel}
                        </DialogTitle>
                        <Box p={2}>
                            <InscripcionesAdmin cursoId={cursoSel} />
                        </Box>
                    </Dialog>
                </>
            )}

            {/* ------------ TAB: MAESTROS ------------ */}
            {tab === 1 && (
                <Box>
                    <MaestrosAdmin onChanged={fetchMaestros} />
                </Box>
            )}
        </Box>
    );
}
